import { dateAdjustedForTimezone } from '@opallabs/lambda'
import {
  PartialContent,
  PartialMoment,
  contentHasAccount,
  contentHasLabelArray,
  contentHasScheduledAt,
  contentHasScheduledTime,
  contentHasService,
  labelHasNameAndSet,
} from '../momentTypes'
import { formatHourId, NO_GROUPING_CATEGORY_ID } from '../sortHelpers'
import { WeekViewSortType } from '../types'

/**
 * Gets categories for a single content item. Mirrors `MomentGetter` from
 * `momentGetters.ts` but runs against a content item's own attributes.
 *
 * Time of Day buckets by the hour of the content's own `scheduled_at`
 * (e.g. 9am, 10am) — distinct from the moment-side getter, which uses
 * the moment's coarser `day_segment` string.
 */
export type ContentGetter = (
  content: PartialContent,
  parentMoment: PartialMoment | undefined,
  parentCategory?: string
) => string[] | null

const getContentTime: ContentGetter = (content) => {
  if (!contentHasScheduledAt(content)) return null
  // No chosen time — bucket under the null "Unscheduled" row, not 12 AM.
  if (!contentHasScheduledTime(content)) return null
  // Route through the same timezone helper that `contentMap` uses for day
  // placement so Time-of-Day buckets stay aligned with the day the card
  // renders on for users whose local timezone differs from the workspace's.
  const adjusted = dateAdjustedForTimezone(content.scheduled_at)
  if (!adjusted) return null
  return [formatHourId(adjusted.getHours())]
}

const getContentNone: ContentGetter = () => [NO_GROUPING_CATEGORY_ID]

const getContentChannel: ContentGetter = (content) => {
  if (!contentHasService(content)) return null
  return [content.service.system_name]
}

const getContentAccount: ContentGetter = (content) => {
  if (!contentHasAccount(content)) return null
  return [content.account.name]
}

const getContentLabels: ContentGetter = (
  content,
  parentMoment,
  parentCategory
) => {
  if (!parentCategory) {
    throw new Error(
      'Target Label Set is required when grouping Content by Label Set.'
    )
  }

  // `parentCategory` is the label-set *id* for this branch. Match by id, not
  // name, so two sets that share a display name aren't conflated — and so
  // placement agrees with `buildLabelSetCategories`, which builds the rows by
  // id (a card matched by name into a row that doesn't exist is orphaned).
  const inTargetSet = (label: { label_set: { id?: string } }): boolean =>
    String(label.label_set.id ?? '') === parentCategory

  // Labels like "Pillar" usually sit at the moment level, not the post
  // level — fall back to parent moment's labels when content has none.
  const contentLabels = contentHasLabelArray(content)
    ? content.labels.filter(labelHasNameAndSet)
    : []
  const matchingContentLabels = contentLabels.filter(inTargetSet)

  if (matchingContentLabels.length > 0) {
    return matchingContentLabels.map<string>((label) => label.name)
  }

  const momentLabels = (parentMoment?.labels ?? []).filter(labelHasNameAndSet)
  const matchingMomentLabels = momentLabels.filter(inTargetSet)
  if (matchingMomentLabels.length > 0) {
    return matchingMomentLabels.map<string>((label) => label.name)
  }

  return null
}

export const makeContentCategoryGetter = (
  groupBy: WeekViewSortType
): ContentGetter => {
  switch (groupBy) {
    case WeekViewSortType.Time:
      return getContentTime
    case WeekViewSortType.Channel:
      return getContentChannel
    case WeekViewSortType.Account:
      return getContentAccount
    case WeekViewSortType.LabelSet:
      return getContentLabels
    case WeekViewSortType.None:
      return getContentNone
  }
}
